import {
  IconCheck,
  IconClock,
  IconCoins,
  IconCopy,
  IconGift,
  IconLock,
  IconRocket,
  IconSparkles,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { trackRetentionEvent } from '@/analytics/retention-events';
import { authClient } from '@/auth/client';
import { RegisterForm } from '@/components/auth/register-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DAILY_CHECK_IN_ENABLED } from '@/config/credit-economics';
import { websiteConfig } from '@/config/website';
import {
  DAILY_CHECK_IN_JOURNEY_DAYS,
  getDailyCheckInReward,
} from '@/credits/daily-check-in-policy';
import {
  useClaimDailyCheckIn,
  useDailyCheckIn,
} from '@/hooks/use-daily-check-in';
import {
  DAILY_CHECK_IN_OPEN_EVENT,
  type DailyCheckInEntrySource,
  type DailyCheckInOpenDetail,
  withDailyCheckInIntent,
} from '@/lib/daily-check-in-dialog';
import { getFreeImageTrialHref } from '@/lib/free-image-trial';
import { getLocale, localizeHref } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';

type DialogMode = 'journey' | 'register';

function getCurrentPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function formatCountdown(target: string | null) {
  if (!target) return '00:00:00';
  const remaining = Math.max(0, new Date(target).getTime() - Date.now());
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((value) => value.toString().padStart(2, '0'))
    .join(':');
}

function useCountdown(target: string | null) {
  const [value, setValue] = useState(() => formatCountdown(target));

  useEffect(() => {
    setValue(formatCountdown(target));
    if (!target) return;
    const interval = window.setInterval(
      () => setValue(formatCountdown(target)),
      1000
    );
    return () => window.clearInterval(interval);
  }, [target]);

  return value;
}

function getRewardLabel(day: number, locale: ReturnType<typeof getLocale>) {
  const reward = getDailyCheckInReward(day);
  if (reward.type === 'credit') {
    return m.check_in_credit_reward({ credits: reward.credits }, { locale });
  }
  if (reward.type === 'creative_prompt') {
    return m.check_in_prompt_reward({}, { locale });
  }
  return m.check_in_complete_reward({}, { locale });
}

function RewardGrid({
  active,
  completedDays,
  nextDay,
}: {
  active: boolean;
  completedDays: number;
  nextDay: number | null;
}) {
  const locale = getLocale();
  const rewardRailRef = useRef<HTMLFieldSetElement>(null);
  const currentDayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rewardRail = rewardRailRef.current;
    const currentDay = currentDayRef.current;
    if (
      !active ||
      !rewardRail ||
      !currentDay ||
      !window.matchMedia('(max-width: 639px)').matches
    ) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const railRect = rewardRail.getBoundingClientRect();
      const currentRect = currentDay.getBoundingClientRect();
      rewardRail.scrollTo({
        left:
          rewardRail.scrollLeft +
          currentRect.left -
          railRect.left -
          (railRect.width - currentRect.width) / 2,
        behavior: 'auto',
      });
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [active, nextDay]);

  return (
    <fieldset
      ref={rewardRailRef}
      data-testid="check-in-reward-rail"
      className="m-0 flex w-full min-w-0 max-w-full snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain border-0 p-0 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-7 sm:gap-2 sm:overflow-visible sm:pb-0"
      aria-label={m.check_in_days_completed(
        { days: completedDays },
        { locale }
      )}
    >
      {Array.from({ length: DAILY_CHECK_IN_JOURNEY_DAYS }, (_, index) => {
        const day = index + 1;
        const reward = getDailyCheckInReward(day);
        const completed = day <= completedDays;
        const current = day === nextDay;
        return (
          <div
            key={day}
            data-check-in-day={day}
            ref={current ? currentDayRef : undefined}
            aria-current={current ? 'step' : undefined}
            className={cn(
              'relative flex min-h-20 w-[4.75rem] shrink-0 snap-center flex-col items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-center sm:w-auto sm:px-1.5 sm:py-2',
              completed &&
                'border-emerald-400/65 bg-emerald-400/12 text-emerald-100',
              current &&
                'border-amber-300 bg-amber-300/12 text-amber-100 shadow-[0_0_24px_rgba(251,191,36,0.18)]',
              !completed &&
                !current &&
                'border-white/10 bg-white/[0.035] text-white/42'
            )}
          >
            <span className="font-medium text-[9px] text-current/75 sm:text-[10px]">
              Day {day}
            </span>
            <span className="flex size-6 items-center justify-center rounded-full bg-black/20 sm:size-7">
              {completed ? (
                <IconCheck className="size-3.5 sm:size-4" />
              ) : reward.type === 'credit' ? (
                <IconCoins className="size-3.5 sm:size-4" />
              ) : reward.type === 'creative_prompt' ? (
                <IconGift className="size-3.5 sm:size-4" />
              ) : (
                <IconLock className="size-3.5 sm:size-4" />
              )}
            </span>
            <span className="line-clamp-2 max-w-full font-semibold text-[9px] leading-tight sm:text-[10px]">
              {getRewardLabel(day, locale)}
            </span>
          </div>
        );
      })}
    </fieldset>
  );
}

export function GuestCreditOfferDialog() {
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user;
  const statusQuery = useDailyCheckIn();
  const claimMutation = useClaimDailyCheckIn();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<DialogMode>('journey');
  const [callbackUrl, setCallbackUrl] = useState<string>();
  const [entrySource, setEntrySource] =
    useState<DailyCheckInEntrySource>('free_credits');
  const [copied, setCopied] = useState(false);
  const authEnabled = websiteConfig.auth?.enable ?? false;
  const locale = getLocale();
  const status = statusQuery.data;
  const countdown = useCountdown(status?.nextEligibleAt ?? null);
  const completedDays = status?.completedDays ?? 0;
  const nextDay = status?.nextDay ?? (user ? null : 1);
  const latestCreativeReward = status?.latestClaim?.creativeReward;

  const signInCallback = useMemo(() => {
    if (typeof window === 'undefined') return getFreeImageTrialHref();
    return withDailyCheckInIntent(callbackUrl ?? getCurrentPath());
  }, [callbackUrl]);

  useEffect(() => {
    if (!authEnabled || !DAILY_CHECK_IN_ENABLED) return;

    function handleOpen(event: Event) {
      const detail = (event as CustomEvent<DailyCheckInOpenDetail>).detail;
      setCallbackUrl(detail?.callbackUrl);
      setEntrySource(detail?.source ?? 'free_credits');
      setMode('journey');
      setOpen(true);
      trackRetentionEvent('daily_check_in_view', {
        authenticated: !!user,
        source: detail?.source ?? 'free_credits',
      });
    }

    window.addEventListener(DAILY_CHECK_IN_OPEN_EVENT, handleOpen);
    return () =>
      window.removeEventListener(DAILY_CHECK_IN_OPEN_EVENT, handleOpen);
  }, [authEnabled, user]);

  useEffect(() => {
    if (!authEnabled || isPending || !DAILY_CHECK_IN_ENABLED) return;
    const search = new URLSearchParams(window.location.search);
    if (user && search.get('checkIn') === '1') {
      search.delete('checkIn');
      const nextUrl = `${window.location.pathname}${
        search.size ? `?${search.toString()}` : ''
      }${window.location.hash}`;
      window.history.replaceState(window.history.state, '', nextUrl);
      setEntrySource('free_credits');
      setMode('journey');
      setOpen(true);
      trackRetentionEvent('daily_check_in_view', {
        authenticated: true,
        source: 'free_credits',
      });
    }
  }, [authEnabled, isPending, user]);

  async function claimReward() {
    const nextStatus = await claimMutation.mutateAsync();
    const claim = nextStatus.latestClaim;
    if (claim) {
      trackRetentionEvent('daily_check_in_claim', {
        credits: claim.creditsAwarded,
        day: claim.day,
        reward_type: claim.rewardType,
        source: entrySource,
      });
    }
  }

  async function copyPrompt() {
    if (!latestCreativeReward) return;
    await navigator.clipboard.writeText(latestCreativeReward.prompt);
    setCopied(true);
    trackRetentionEvent('daily_check_in_reward_copy', {
      day: status?.latestClaim?.day,
      reward_key: latestCreativeReward.key,
    });
    window.setTimeout(() => setCopied(false), 1800);
  }

  function openRegister() {
    trackRetentionEvent('daily_check_in_auth_start', { source: entrySource });
    setMode('register');
  }

  if (!authEnabled || isPending) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setMode('journey');
          setCopied(false);
        }
      }}
    >
      {mode === 'register' && !user ? (
        <DialogContent className="overflow-hidden border-0 p-0 sm:max-w-100">
          <DialogHeader className="sr-only">
            <DialogTitle>{m.check_in_guest_title({}, { locale })}</DialogTitle>
          </DialogHeader>
          <RegisterForm
            callbackUrl={signInCallback}
            creditOffer={{ kind: 'signup' }}
            showBottomLink={false}
          />
        </DialogContent>
      ) : (
        <DialogContent className="flex max-h-[calc(100svh-1rem)] flex-col overflow-hidden border border-amber-300/20 bg-[#12151a] p-0 text-white shadow-[0_32px_120px_rgba(0,0,0,0.7)] sm:max-h-[calc(100svh-2rem)] sm:max-w-[56rem]">
          <div className="shrink-0 border-white/8 border-b bg-[#191d23] px-4 py-5 sm:px-7 sm:py-6">
            <DialogHeader className="items-center text-center">
              <span className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 font-semibold text-amber-200 text-xs">
                <IconSparkles className="size-3.5" />
                {m.check_in_badge({}, { locale })}
              </span>
              <DialogTitle className="font-bold text-xl leading-tight sm:text-3xl">
                {user
                  ? m.check_in_member_title({}, { locale })
                  : m.check_in_guest_title({}, { locale })}
              </DialogTitle>
              <DialogDescription className="max-w-2xl text-white/62 leading-relaxed">
                {user
                  ? m.check_in_member_description({}, { locale })
                  : m.check_in_guest_description({}, { locale })}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/6 px-3 py-1.5 text-white/72">
                <IconCoins className="size-4 text-amber-300" />
                {m.check_in_earned(
                  { credits: status?.creditsEarned ?? 0 },
                  { locale }
                )}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/6 px-3 py-1.5 text-white/72">
                <IconGift className="size-4 text-emerald-300" />
                {m.check_in_days_completed({ days: completedDays }, { locale })}
              </span>
            </div>

            <RewardGrid
              active={open}
              completedDays={completedDays}
              nextDay={nextDay}
            />

            {latestCreativeReward ? (
              <div className="border-white/10 border-t pt-5">
                <div className="flex items-start gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-emerald-400/12 text-emerald-300">
                    <IconGift className="size-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base">
                      {latestCreativeReward.title}
                    </h3>
                    <p className="mt-1 text-sm text-white/55">
                      {latestCreativeReward.description}
                    </p>
                    <p className="mt-3 max-h-24 overflow-y-auto border-white/10 border-l-2 pl-3 text-white/72 text-xs leading-relaxed">
                      {latestCreativeReward.prompt}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={copyPrompt}
                        className="inline-flex h-9 items-center gap-2 rounded-md border border-white/12 px-3 font-semibold text-xs transition hover:bg-white/7"
                      >
                        {copied ? (
                          <IconCheck className="size-4 text-emerald-300" />
                        ) : (
                          <IconCopy className="size-4" />
                        )}
                        {copied
                          ? m.check_in_copied({}, { locale })
                          : m.check_in_copy_prompt({}, { locale })}
                      </button>
                      <a
                        href={localizeHref(
                          getFreeImageTrialHref(latestCreativeReward.prompt)
                        )}
                        className="inline-flex h-9 items-center gap-2 rounded-md bg-white px-3 font-semibold text-[#15171b] text-xs transition hover:bg-white/90"
                      >
                        <IconRocket className="size-4" />
                        {m.check_in_use_prompt({}, { locale })}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div
            data-testid="check-in-action"
            className="shrink-0 border-white/8 border-t bg-[#12151a] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-7 sm:pt-4 sm:pb-[max(1.25rem,env(safe-area-inset-bottom))]"
          >
            {!user ? (
              <button
                type="button"
                onClick={openRegister}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-300 px-5 font-bold text-[#18140a] transition hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/60"
              >
                <IconGift className="size-5" />
                {m.check_in_sign_in({}, { locale })}
              </button>
            ) : statusQuery.isPending ? (
              <div className="h-12 animate-pulse rounded-md bg-white/7" />
            ) : statusQuery.isError || claimMutation.isError ? (
              <div className="rounded-md border border-red-400/25 bg-red-400/8 px-4 py-3 text-center text-red-100 text-sm">
                {m.auth_error_try_again({}, { locale })}
              </div>
            ) : !status?.eligible ? (
              <div className="rounded-md border border-white/10 bg-white/4 px-4 py-3 text-center">
                <p className="font-semibold">
                  {m.check_in_not_eligible_title({}, { locale })}
                </p>
                <p className="mt-1 text-white/52 text-xs">
                  {m.check_in_not_eligible_description({}, { locale })}
                </p>
              </div>
            ) : status.journeyComplete ? (
              <div className="space-y-3 text-center">
                <div>
                  <p className="font-semibold text-emerald-200">
                    {m.check_in_journey_complete_title({}, { locale })}
                  </p>
                  <p className="mt-1 text-white/52 text-xs">
                    {m.check_in_journey_complete_description({}, { locale })}
                  </p>
                </div>
                <a
                  href={localizeHref(Routes.Pricing)}
                  className="inline-flex h-11 w-full items-center justify-center rounded-md bg-white font-bold text-[#15171b] transition hover:bg-white/90"
                >
                  {m.check_in_view_pricing({}, { locale })}
                </a>
              </div>
            ) : status.checkedInToday ? (
              <div className="space-y-2">
                <div className="flex h-12 items-center justify-center gap-2 rounded-md border border-white/8 bg-white/5 px-4 text-white/55">
                  <IconClock className="size-5" />
                  <span className="font-semibold text-sm">
                    {m.check_in_next_in({ time: countdown }, { locale })}
                  </span>
                </div>
                <a
                  href={localizeHref(Routes.Pricing)}
                  className="inline-flex h-9 w-full items-center justify-center rounded-md text-amber-200 text-xs transition hover:bg-amber-300/8 hover:text-amber-100"
                >
                  {m.check_in_view_pricing({}, { locale })}
                </a>
              </div>
            ) : status.nextDay ? (
              <button
                type="button"
                disabled={claimMutation.isPending}
                onClick={claimReward}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-300 px-5 font-bold text-[#18140a] transition hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/60 disabled:cursor-wait disabled:opacity-60"
              >
                <IconGift className="size-5" />
                {m.check_in_claim_button(
                  { reward: getRewardLabel(status.nextDay, locale) },
                  { locale }
                )}
              </button>
            ) : null}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
